import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { C, font } from "../lib/styles.js";
import notify, { Notifications } from "../lib/notify.jsx";
import { BLANK_JOB, BLANK_QUOTE, fmt, initSections, migrateQuote } from "../lib/utils.js";
import { getEqNames } from "../lib/equipment.js";
import { rosterId } from "../lib/holds.js";
import { isActiveQuoteRecord } from "../lib/builderHelpers.js";
import { isProBono, withProBonoTag } from "../lib/proBono.js";
import { snapshotQuoteForVersion } from "../lib/quoteVersions.js";
import { planContactCommits } from "../lib/contactCommit.js";
import { openUnlessCancelled } from "../lib/quotePersistence.js";
import { DataContextTestProvider } from "../context/DataContext.jsx";
import { useQuoteWorkspace } from "../context/useQuoteWorkspace.js";
import UnsavedQuoteDialog from "../components/UnsavedQuoteDialog.jsx";
import { createFakeFirestore } from "./fakeFirestore.js";
import SavedQuotes from "../apps/jobs/views/SavedQuotes.jsx";
import QuoteBuilder from "../apps/jobs/views/QuoteBuilder.jsx";

const CONTACTS = [
  { id: "ct_rina", name: "Rina Patel", company: "Acme Arts LLC", role: ["prospect"] },
  // The holds quotes below are re-saved through the builder, which needs each
  // contact linked to a CRM record (quoteHasLinkedContact).
  { id: "ct_mara", name: "Mara Brooks", company: "Brooks Films", role: ["prospect"] },
  { id: "ct_dave", name: "Dave Sales", company: "Nine Twelve Productions, LLC", role: ["prospect"] },
  { id: "ct_nia", name: "Nia Cole", company: "Harbor Lights Theater", role: ["prospect"] },
  { id: "ct_owen", name: "Owen Reyes", company: "Legacy Gear Co", role: ["prospect"] },
];

// Relative LOCAL dates so follow-up tones (overdue/today/soon) hold whenever —
// and wherever — this runs; toISOString would shift a day in US evenings.
const daysFromNow = (n) => {
  const d = new Date(Date.now() + n * 86400000);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const CLIENTS = [
  { id: "client_acme", name: "Acme Arts LLC", clientCode: "acmarts" },
  { id: "client_brand", name: "Booked Brand Inc", clientCode: "boobra" },
];

const QUOTES = [
  {
    id: "q_active",
    clientName: "Acme Arts LLC",
    contactName: "Rina Patel",
    companyEntityId: "client_acme",
    contactEntityId: "ct_rina",
    quoteDate: "2026-06-01",
    scanDate: "2026-06-02",
    jobStatus: "quoted",
    followUpDate: daysFromNow(-3),
    total: 12450,
    summary: "Two-day full body capture",
    jobLocation: "Pier 59 Studios",
    equipment: ["Big Rig", { name: "Canon R5" }],
    sessions: [
      { id: "sess_setup", sessionType: "setup", date: "2026-06-02", locationVenue: "Loading dock" },
      { id: "sess_capture", sessionType: "capture", date: "2026-06-03", callTime: "09:00" },
    ],
    // Slice 2: seeded CRM contact-match review so the Contacts panel renders in
    // local review (no auth/AI needed). Lean shape — same as buildContactMatches.
    contactMatches: [
      { extracted: { name: "Rina Patel", email: "rina@acmearts.com", url: "", role: "contact", source: "email_from" }, confidence: "high", matchId: "ct_rina", matchName: "Rina Patel", origMatchId: "ct_rina", origMatchName: "Rina Patel", confirmed: false, ignored: false },
      { extracted: { name: "Acme Arts", email: "", url: "acmearts.com", role: "client", source: "email_signature" }, confidence: "medium", matchId: "client_acme", matchName: "Acme Arts LLC", origMatchId: "client_acme", origMatchName: "Acme Arts LLC", confirmed: false, ignored: false },
      { extracted: { name: "Jordan Vance", email: "jordan@newprod.io", url: "", role: "coordinator", source: "email_cc" }, confidence: "low", matchId: null, matchName: null, origMatchId: null, origMatchName: null, confirmed: false, ignored: false },
    ],
  },
  {
    id: "q_booked",
    clientName: "Booked Brand Inc",
    contactName: "Morgan Lee",
    quoteDate: "2026-05-18",
    scanDate: "2026-05-28",
    jobStatus: "booked",
    total: 3800,
    summary: "Product scan package",
    jobLocation: "New York Capture",
    equipment: ["Artec Leo"],
  },
  {
    id: "q_archived",
    clientName: "Archived Client",
    contactName: "Casey Drew",
    quoteDate: "2026-04-02",
    scanDate: "2026-04-14",
    jobStatus: "archived",
    total: 900,
    summary: "Archived quote should stay out of open worklist",
    jobLocation: "Queens",
  },
  {
    // The earliest legacy quoter wrote a literal "TBD" scanDate. The list must
    // treat it as undated (row label "TBD") — never sort it on top or render an
    // "Invalid Date" month header.
    id: "q_tbd_legacy",
    clientName: "Legacy TBD Films",
    contactName: "Sam Older",
    quoteDate: "2026-03-05",
    scanDate: "TBD",
    jobStatus: "quoted",
    total: 1500,
    summary: "Legacy quote with a TBD capture date",
  },
  {
    id: "q_lost",
    clientName: "Lost Deal Co",
    contactName: "Pat Doyle",
    quoteDate: "2026-05-20",
    scanDate: "2026-06-20",
    jobStatus: "lost",
    total: 5400,
    summary: "Went with a competitor",
    updatedAt: "2026-05-25T10:00:00.000Z",
  },
  {
    // Completed with NO live job = "Won" in Closed (legacy let you hand-set this).
    id: "q_won",
    clientName: "Won Opera Co",
    contactName: "Hana Lim",
    quoteDate: "2025-11-01",
    scanDate: "2025-11-20",
    jobStatus: "completed",
    total: 16500,
    summary: "Stage set capture — delivered",
    updatedAt: "2025-12-01T10:00:00.000Z",
  },
  {
    // Booked but its job was deleted = zombie. Stays visible in Open with a
    // warning, and is EXCLUDED from the pipeline number.
    id: "q_zombie",
    clientName: "Zombie Pictures",
    contactName: "R. Calloway",
    quoteDate: "2026-05-01",
    scanDate: "2026-06-30",
    jobStatus: "booked",
    total: 6800,
    summary: "Booked in May; the linked job was deleted",
  },
];

const TRASHED = [
  {
    id: "q_trashed",
    clientName: "Trashed Co",
    contactName: "",
    quoteDate: "2026-05-10",
    jobStatus: "quoted",
    total: 700,
    summary: "Deleted by mistake",
    deletedAt: "2026-06-02T09:00:00.000Z",
  },
];

const BOOKED_JOB = {
  id: "j_existing",
  quoteId: "q_booked",
  clientName: "Booked Brand Inc",
  jobId: "boobra_260528",
  captureDate: "2026-05-28",
};

const DRAFTS = [
  {
    id: "q_draft_jf",
    draft: true,
    clientName: "JF Bouchard",
    contactName: "Jean-François Bouchard",
    quoteDate: "2026-06-01",
    jobStatus: "quoted",
    total: 2400,
    summary: "Full body scan — sculpture reference",
    lineItems: [{ description: "Full-body scan — 1 subject, in studio", qty: 1, rate: 2400 }],
    savedAt: "2026-06-01T12:00:00.000Z",
  },
];

// ?e2e=quotes&lidar=1 — LiDAR v2 scenario seeds (2026-09-03), OFF by default so the
// pipeline counts other specs assert stay untouched: an archived quote priced by the v1
// tier matrix (its PDF must keep the frozen v1 addendum) and a New Quote that opens the
// builder with the Sharp brief already extracted (quote.lidarPrefill), so the estimator
// auto-opens pre-filled exactly as it does after a real extraction.
const LIDAR_PARAM = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("lidar") : null;
const LIDAR_SCENARIO = LIDAR_PARAM === "1";
// ?e2e=quotes&lidar=2 — LiDAR blocks (2026-09-03): a brief with two scopes priced on their
// own, plus a custom line the notes assigned to the second one. The estimator must open on
// block 1 with chips for both, insert and move on to block 2, close after it, and the list
// and PDF must group the lines under each block with a subtotal.
const LIDAR_BLOCKS = LIDAR_PARAM === "2";
const SHARP_PREFILL = {
  mode: "processing", dayType: "film", crewSize: 3, photogrammetry: true, hoursOnSite: 10, timeBand: "full", overtimeHrs: 0, setupCount: 35, rush: true,
  evidence: { setupCount: "25-35 setups", crewSize: "3 on staff", hoursOnSite: "on set for the full shoot day, about 10 hours", rush: "models back Monday" },
};
const BLOCKS_PREFILL = [
  { label: "Lobby", note: "Monday, October 5", mode: "processing", dayType: "film", crewSize: 1, photogrammetry: false, setupCount: 20, evidence: { setupCount: "about 20 setups in the lobby" } },
  { label: "Roof", mode: "processing", dayType: "film", crewSize: 2, photogrammetry: true, setupCount: 10, evidence: { crewSize: "two of us on the roof" } },
];
const seedLidarQuote = () => LIDAR_SCENARIO ? {
  ...BLANK_QUOTE(), step: "build", industry: "film_tv", contactName: "Randy Starr", productionName: "Sharp",
  jobLocation: "225 W 44th St, New York, NY", scanDate: "2026-09-11", lidarDetected: true, lidarPrefill: SHARP_PREFILL,
} : LIDAR_BLOCKS ? {
  ...BLANK_QUOTE(), step: "build", industry: "film_tv", contactName: "Dana Frost", clientName: "Frost Productions", productionName: "Blocks",
  jobLocation: "1 Main St, Brooklyn, NY", scanDate: "2026-10-05", lidarDetected: true, lidarPrefill: BLOCKS_PREFILL,
  lineGroupNotes: { Lobby: "Monday, October 5" },
  lineGroups: [{ label: "Lobby", note: "Monday, October 5" }, { label: "Roof", note: "" }],
  lineItems: [{ key: "li_roof_stills", catalogId: "custom", description: "Roof drone stills", notes: "Twelve frames for reference", qty: 1, rate: 500, discount: null, group: "Roof" }],
} : null;
const V1_LIDAR_QUOTE = {
  id: "q_lidar_v1", clientName: "Frost Productions", contactName: "Dana Frost", quoteDate: "2026-08-26", scanDate: "2026-09-20",
  jobStatus: "quoted", followUpDate: daysFromNow(5), total: 5850, summary: "Gilder Center atrium LiDAR and photogrammetry",
  jobLocation: "American Museum of Natural History", deliverableFormats: ["obj"], sections: initSections(),
  // Real v1 line shape as stored in Firestore by the retired estimator: no lidar.model stamp.
  lineItems: [{ catalogId: "lidar_estimate", description: "LiDAR — Capture + Processing (RTC 360)", notes: "Capture + processing · Full day (3–8h) · 75 setups (61-99 tier) · + photogrammetry", qty: 1, rate: 5850, discount: null,
    lidar: { mode: "processing", timeBand: "full", setupCount: 75, setupTier: "61-99", photogrammetry: true, highDensity: false, texture: "r5", baseCell: 5850, highDensityMultiplier: 1, processingDays: 5, rate: 5850 } }],
};

// ?e2e=quotes&holds=1: holds (2026-09-04), OFF by default so the pipeline counts
// other specs assert stay untouched. Two open quotes share the drone on Oct 7 (one
// partial, one full) and Peter; a booked job on Oct 6 carries the Canon R5 and John
// Cushman; a switched-off quote on Oct 6 is there to be turned on against that job;
// a legacy quote carries an equipment string nobody recognizes. The roster is the
// live Settings shape plus the derived id useData() exposes.
const HOLDS_SCENARIO = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("holds") === "1";
const HOLD_ROSTER = [
  { name: "John Cushman", email: "john@nycap3d.com", phone: "", receivesAllUpdates: true },
  { name: "Peter Richtarech", email: "", phone: "", receivesAllUpdates: false },
  { name: "Chris Morel", email: "", phone: "", receivesAllUpdates: false },
  { name: "Chris Kitchen", email: "", phone: "", receivesAllUpdates: false },
].map(entry => ({ ...entry, id: rosterId(entry) }));
const HOLD_QUOTES = [
  {
    id: "q_1000004812", clientName: "Brooks Films", contactName: "Mara Brooks", contactEntityId: "ct_mara", quoteDate: "2026-09-01", jobStatus: "quoted",
    followUpDate: daysFromNow(9), total: 4200, summary: "Rooftop drone survey, partial day", jobLocation: "Red Hook",
    multiDay: true, scanDate: "2026-10-07",
    sessions: [{ id: "sess_brooks_cap", sessionType: "capture", date: "2026-10-07", dayType: "partial", location: "Red Hook" }],
    equipmentIds: ["drone"], crew: [{ id: "name:peter-richtarech", name: "Peter Richtarech" }], holdOff: false,
    lineItems: [{ description: "Drone survey, partial day", qty: 1, rate: 4200 }], savedAt: "2026-09-01T10:00:00.000Z",
  },
  {
    id: "q_1000001102", clientName: "Nine Twelve Productions, LLC", contactName: "Dave Sales", contactEntityId: "ct_dave", quoteDate: "2026-09-03", jobStatus: "quoted",
    followUpDate: daysFromNow(9), total: 23850, summary: "Four October capture days, LiDAR and drone", jobLocation: "Fresh Kills",
    multiDay: true, scanDate: "2026-10-07",
    sessions: [
      { id: "sess_912_cap1", sessionType: "capture", date: "2026-10-07", dayType: "full", location: "Fresh Kills" },
      { id: "sess_912_cap2", sessionType: "capture", date: "2026-10-17", dayType: "full", location: "Tribeca" },
    ],
    equipmentIds: ["rtc", "drone"],
    crew: [{ id: "name:peter-richtarech", name: "Peter Richtarech" }, { id: "name:chris-morel", name: "Chris Morel" }],
    holdOff: false,
    lineItems: [{ description: "LiDAR capture, Fresh Kills", qty: 1, rate: 23850 }], savedAt: "2026-09-03T10:00:00.000Z",
  },
  {
    id: "q_1000000206", clientName: "Harbor Lights Theater", contactName: "Nia Cole", contactEntityId: "ct_nia", quoteDate: "2026-09-02", jobStatus: "quoted",
    followUpDate: daysFromNow(9), total: 2400, summary: "Set reference stills", jobLocation: "Navy Yard", scanDate: "2026-10-06",
    equipmentIds: [], crew: [], holdOff: true,
    lineItems: [{ description: "Reference photography, full day", qty: 1, rate: 2400 }], savedAt: "2026-09-02T10:00:00.000Z",
  },
  {
    id: "q_1000000777", clientName: "Legacy Gear Co", contactName: "Owen Reyes", contactEntityId: "ct_owen", quoteDate: "2026-09-02", jobStatus: "quoted",
    followUpDate: daysFromNow(9), total: 900, summary: "Quoted by the old quoter, equipment as strings", scanDate: "2026-11-03",
    equipment: ["Truck Scanner", "Big Rig"],
    lineItems: [{ description: "Studio session", qty: 1, rate: 900 }], savedAt: "2026-09-02T10:00:00.000Z",
  },
].map(migrateQuote);
const HOLD_JOB = {
  id: "j_atl", quoteId: null, clientName: "Atlantic Power Solutions", jobId: "atlpow_261006", clientCode: "atlpow",
  captureDate: "2026-10-06", status: "scheduled", equipment: ["Canon R5"], selectedEquipment: ["Canon R5"], crew: "John Cushman",
};


// ?e2e=quotes&box=1: the AI box (tests/e2e-box, built with VITE_QUOTE_AI_INPUT=1), OFF by
// default so other specs' counts stay untouched. One saved quote with a linked contact and
// client: a catalog line that carries its catalog note (L1), a LiDAR line the box must
// refuse to touch (L2), a custom line it may remove (L3), and one earlier version. The
// second CRM client (boobra) is there for a code that moves the quote.
const BOX_SCENARIO = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("box") === "1";
const BOX_QUOTE = {
  id: "q_box",
  clientName: "Acme Arts LLC",
  contactName: "Rina Patel",
  contactEmail: "rina@example.test",
  companyEntityId: "client_acme",
  contactEntityId: "ct_rina",
  quoteDate: "2026-06-01",
  scanDate: "2026-06-12",
  jobStatus: "quoted",
  followUpDate: daysFromNow(5),
  total: 7750,
  summary: "Box scenario sculpture scan",
  jobLocation: "Acme Arts warehouse, Queens",
  deliverableText: "One watertight model, OBJ.",
  sections: initSections(),
  lineItems: [
    { catalogId: "env_nimble_onloc", description: "On-Location Scanning Session, Partial Day (up to 3 hrs)", notes: "Handheld scanning crew on site. Deliverables billed per model on top.", qty: 1, rate: 1400 },
    { catalogId: "lidar_estimate", description: "LiDAR — Capture + Processing (RTC 360)", notes: "Capture + processing · Full day (3–8h) · 75 setups (61-99 tier) · + photogrammetry", qty: 1, rate: 5850, discount: null,
      lidar: { mode: "processing", timeBand: "full", setupCount: 75, setupTier: "61-99", photogrammetry: true, highDensity: false, texture: "r5", baseCell: 5850, highDensityMultiplier: 1, processingDays: 5, rate: 5850 } },
    { catalogId: "custom", description: "Model cleanup", notes: "", qty: 1, rate: 500 },
  ],
  savedAt: "2026-06-01T12:00:00.000Z",
};
BOX_QUOTE.versions = [snapshotQuoteForVersion({ ...BOX_QUOTE, summary: "Box scenario, an earlier version" }, { id: "v_box", source: "amend", timestamp: "2026-05-20T12:00:00.000Z" })];

// ?e2e=quotes&keep=1: the fake Firestore survives a page reload (sessionStorage), so a
// spec can prove what a reload does with the recovery copy. Off by default so every
// other spec starts from the seed.
const KEEP = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("keep") === "1";
const STORE_KEY = `e2e-quotes-store:${typeof window !== "undefined" ? window.location.search : ""}`;
const persistStore = KEEP ? {
  load: () => { try { const raw = window.sessionStorage.getItem(STORE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; } },
  save: (docs) => { try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(docs)); } catch { /* full or blocked */ } },
} : null;

const seedQuotes = () => (LIDAR_SCENARIO ? [...QUOTES, V1_LIDAR_QUOTE] : HOLDS_SCENARIO ? [...QUOTES, ...HOLD_QUOTES] : BOX_SCENARIO ? [...QUOTES, BOX_QUOTE] : QUOTES);
const seedStore = () => ({
  quotes: [...seedQuotes(), ...DRAFTS, ...TRASHED].map(migrateQuote),
  jobs: HOLDS_SCENARIO ? [BOOKED_JOB, HOLD_JOB] : [BOOKED_JOB],
  contacts: CONTACTS,
  clients: CLIENTS,
});

// The fake Firestore is module state, so a React remount (StrictMode) keeps the same
// documents, and the spec can reach it to inject a slow or failing write.
let fakeStore = null;
const getFakeStore = () => {
  if (!fakeStore) {
    fakeStore = createFakeFirestore({ persist: persistStore, initial: seedStore() });
    if (typeof window !== "undefined") window.__quotesFake = fakeStore;
  }
  return fakeStore;
};

export default function E2EQuotesFixture() {
  const fake = getFakeStore();
  const [quotes, setQuotesState] = useState(() => fake.reload().quotes || []);
  const quotesLive = useRef(quotes);
  quotesLive.current = quotes;
  const setQuotes = useCallback((update) => {
    quotesLive.current = update(quotesLive.current);
    setQuotesState(quotesLive.current);
  }, []);
  const [jobs, setJobs] = useState(() => fake.reload().jobs || []);
  const [contacts, setContacts] = useState(() => fake.reload().contacts || []);
  const [clients, setClients] = useState(() => fake.reload().clients || []);
  const [openedJob, setOpenedJob] = useState(null);
  const [view, setView] = useState("saved");

  // The CRM commit the real save runs, against the fake store.
  const commitContactMatches = async (quote, matches) => {
    const plan = planContactCommits(quote, matches, contacts, clients);
    const written = { contacts: [], clients: [] };
    for (const op of plan.ops) {
      if (op.kind === "client") { await fake.clients.save(op.record); written.clients.push(op.record); setClients(prev => [...prev.filter(c => c.id !== op.record.id), op.record]); }
      else { await fake.contacts.save(op.record); written.contacts.push(op.record); setContacts(prev => [...prev.filter(c => c.id !== op.record.id), op.record]); }
    }
    return { ...plan, written, failed: [] };
  };

  const workspace = useQuoteWorkspace({
    isAdmin: true, loaded: true,
    quotes, getQuotes: () => quotesLive.current, setQuotes, jobs, contacts, clients,
    storage: fake,
    getImages: async () => null,
    saveImages: async () => {},
    deleteAttachmentFile: async () => {},
    notify,
    commitContactMatches,
    syncJobRevenueFromQuote: async () => ({ synced: false, skipped: false, reason: "no-linked-job" }),
    getEquipmentMap: () => getEqNames(),
    // Short in the browser tests, so a stuck write shows its message within a test's patience.
  }, { lockTimeoutMs: 4000 });
  const { q, setQ, getQ, busy, unsavedPrompt, persistence, setBuilderSaveBlocker, highlightMissing, setHighlightMissing } = workspace;

  // The recovery copy a reload would find, installed once, the way DataContext does.
  const recovered = useRef(false);
  useEffect(() => {
    if (recovered.current) return;
    recovered.current = true;
    fake.kv.get("nycap-draft").then((copy) => { if (copy) persistence.installRecoveredCopy(copy); });
  }, [fake, persistence]);

  const savedQuotes = useMemo(() => quotes.filter(sq => !sq.deletedAt && isActiveQuoteRecord(sq)), [quotes]);
  const draftQuotes = useMemo(() => quotes.filter(sq => !sq.deletedAt && !isActiveQuoteRecord(sq)), [quotes]);
  const trashedQuotes = useMemo(() => quotes.filter(sq => !!sq.deletedAt), [quotes]);

  const permanentDeleteQuote = async (id) => {
    // As DataContext: a quote being saved, booked or opened, or the one open in the builder,
    // can't be deleted from the list.
    if (persistence.isLocked(id)) { notify.error("This quote is still saving, booking or opening. Try again when it has finished."); return false; }
    if (persistence.isOpenQuote(id)) { notify.error("This quote is open in the builder. Open another quote (or New) first, then delete it."); return false; }
    await fake.quotes.delete(id);
    setQuotes(prev => prev.filter(x => x.id !== id));
    return true;
  };
  const deleteDraft = permanentDeleteQuote;
  // What the other computer does, for the spec: a delete or a list action there reaches
  // this tab as the quotes listener would deliver it (the store, then the list, then the
  // open builder through followListSnapshot).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__quotesElsewhere = {
      deleteQuote: async (id) => {
        await fake.quotes.delete(id);
        const records = fake.reload().quotes || [];
        setQuotes(() => records);
        persistence.followListSnapshot(records);
      },
      patchQuote: async (id, fields) => {
        await fake.quotes.patch(id, fields);
        const records = fake.reload().quotes || [];
        setQuotes(() => records);
        persistence.followListSnapshot(records);
      },
    };
    return () => { delete window.__quotesElsewhere; };
  }, [fake, persistence]);
  const markFollowedUp = (id) => persistence.patchQuoteFields(id, { followUpDate: "" });
  const snoozeBizDays = (id, n) => persistence.patchQuoteFields(id, { followUpDate: daysFromNow(n) });
  const snoozeToDate = (id, dateStr) => persistence.patchQuoteFields(id, { followUpDate: dateStr });

  const dataValue = useMemo(() => ({
    q, setQ, getQ,
    savedQuotes, draftQuotes, trashedQuotes,
    jobs, contacts, clients,
    crewRoster: HOLDS_SCENARIO ? HOLD_ROSTER : [],
    equipmentMap: getEqNames(),
    // Holds slice 2 contract: the projected hold records and the normalized
    // role useData() exposes. The quotes surfaces are admin-only.
    holds: [],
    role: "admin",
    quotesLoading: false,
    quotesError: "",
    highlightMissing, setHighlightMissing,
    // The real saving module (lib/quotePersistence.js) on the fake store.
    saveQuote: persistence.saveQuote,
    saveDraft: persistence.saveDraft,
    loadDraft: persistence.loadDraft,
    loadQuote: persistence.loadQuote,
    newQuote: () => (seedLidarQuote() ? persistence.newQuote().then((went) => { if (went) setQ(seedLidarQuote()); return went; }) : persistence.newQuote()),
    duplicateQuote: persistence.duplicateQuote,
    amendQuote: persistence.amendQuote,
    restoreQuoteVersion: persistence.restoreQuoteVersion,
    discardEdits: persistence.discardEdits,
    updateQuoteField: (id, fields) => persistence.patchQuoteFields(id, fields),
    updateQuoteStatus: (id, status) => persistence.patchQuoteFields(id, { jobStatus: status }),
    deleteQuote: (id) => persistence.patchQuoteFields(id, { deletedAt: new Date().toISOString() }),
    restoreQuote: (id) => persistence.patchQuoteFields(id, { deletedAt: null }),
    permanentDeleteQuote,
    deleteDraft,
    markFollowedUp,
    snoozeBizDays,
    snoozeToDate,
    quoteBusy: busy,
    unsavedEditsNotice: persistence.unsavedEditsNotice,
    isQuoteDirty: persistence.isDirty,
    quoteHadRecord: persistence.hadRecord,
    updateQuote: persistence.updateQuote,
    normalizeOpenQuote: persistence.normalizeOpenQuote,
    setBuilderSaveBlocker,
    // Mirror DataContext.saveContact: persist, then push into the local list so the
    // ContactPicker's linked chip resolves immediately (F8/F14 path).
    saveContact: async (c) => { await fake.contacts.save(c); setContacts(prev => [...prev, c]); return c; },
    // Same mirror for the ClientPicker's Add & link path.
    saveClient: async (c) => { await fake.clients.save(c); setClients(prev => [...prev, c]); return c; },
    // Booking through the real lock (whileBooking): the saved row, then the job.
    bookJob: (row, navigateToJob) => persistence.whileBooking(row, async (quote, writeFields) => {
      if (jobs.some(j => j.quoteId === quote.id)) return;
      const job = { ...BLANK_JOB(quote, contacts, clients, { equipmentMap: getEqNames() }), id: `j_from_${quote.id}` };
      await fake.jobs.save(job);
      setJobs(prev => [...prev, job]);
      // As DataContext.bookQuote: the status and the contact link the job was built from.
      await writeFields({ jobStatus: "booked", contactEntityId: quote.contactEntityId || null });
      // Pro bono (2026-09-11): mirrors DataContext.bookJob, which tags every
      // linked contact `pro_bono` when the booked quote is pro bono.
      if (isProBono(quote)) {
        const linked = new Set((job.linkedContacts || []).map(l => l.contactId));
        setContacts(prev => prev.map(c => linked.has(c.id) ? withProBonoTag(c) : c));
      }
      navigateToJob?.(job);
    }),
  }), [q, setQ, getQ, savedQuotes, draftQuotes, trashedQuotes, jobs, contacts, clients, busy, persistence, setBuilderSaveBlocker, highlightMissing, setHighlightMissing, fake]);

  const showBuilder = () => setView("builder");
  return (
    <DataContextTestProvider value={dataValue}>
      <div style={{ minHeight: "100vh", background: C.bg, padding: 28, fontFamily: font }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          {view === "builder" ? (
            <QuoteBuilder onSaved={() => setView("saved")} onOpenSaved={() => setView("saved")} />
          ) : (
            <SavedQuotes
              onOpenJob={setOpenedJob}
              onCreateQuote={() => openUnlessCancelled(() => dataValue.newQuote(), showBuilder, showBuilder)}
              onEditQuote={(quote) => openUnlessCancelled(() => (quote.draft ? persistence.loadDraft(quote.id) : persistence.loadQuote(quote)), showBuilder, showBuilder)}
              onDuplicateQuote={(quote) => openUnlessCancelled(() => persistence.duplicateQuote(quote), showBuilder, showBuilder)}
            />
          )}
          {unsavedPrompt && <UnsavedQuoteDialog prompt={unsavedPrompt} lock={busy} />}
          {openedJob && (
            <div data-testid="opened-job" style={{
              marginTop: 16,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: 14,
              fontSize: 12,
              color: C.text,
            }}>
              Opened job {openedJob.id} · {openedJob.jobId} · billing {openedJob.billing || "standard"} · revenue {fmt(openedJob.grossRevenue || 0)}
              {(openedJob.linkedContacts || []).map(l => {
                const c = contacts.find(x => x.id === l.contactId);
                return c ? <div key={l.contactId} data-testid="opened-job-contact">{c.name} · tags {(c.tags || []).join(", ") || "none"}</div> : null;
              })}
            </div>
          )}
        </div>
        {/* Same toast surface App.jsx mounts — keeps notify.info/error visible
            (and assertable) in e2e instead of silently dropped. */}
        <Notifications />
      </div>
    </DataContextTestProvider>
  );
}
